/**
 * Curated 1970s Music — 30+ questions, audience-calibrated.
 *
 * Bias: Eagles, Stevie Wonder, Carly Simon, Elton John, Carole King,
 * James Taylor, Bee Gees, Marvin Gaye, Aretha, Fleetwood Mac, Linda
 * Ronstadt, Bruce Springsteen, Carpenters, ABBA, Bowie, Diana Ross
 * solo, Earth Wind & Fire, Three Dog Night, John Denver.
 */

import type { TriviaQuestion } from './types';

export const CURATED_MUSIC_70S: TriviaQuestion[] = [
  {
    id: 'c-m70-01',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'The Eagles checked into a "Hotel" of which state in their 1976 hit?',
    options: ['Florida', 'California', 'Nevada', 'Arizona'],
    correctIndex: 1,
    blurb: '"Hotel California" — the album sold over 30 million copies eventually.',
    source: 'curated',
  },
  {
    id: 'c-m70-02',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Carly Simon\'s 1972 hit asked "You\'re So" what?',
    options: ['Sweet', 'Vain', 'Real', 'Wrong'],
    correctIndex: 1,
    blurb: 'The world still argues who it was about — she\'s told two of three answers.',
    source: 'curated',
  },
  {
    id: 'c-m70-03',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Stevie Wonder\'s 1976 double album was called "Songs in the Key of…":',
    options: ['Love', 'Life', 'Soul', 'Light'],
    correctIndex: 1,
    blurb: 'He played most of the instruments himself — and won Album of the Year.',
    source: 'curated',
  },
  {
    id: 'c-m70-04',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Elton John\'s 1973 piano ballad was about "Goodbye Yellow Brick" what?',
    options: ['Road', 'Lane', 'Way', 'House'],
    correctIndex: 0,
    blurb: 'Bernie Taupin wrote the lyric on a napkin in a hotel coffee shop.',
    source: 'curated',
  },
  {
    id: 'c-m70-05',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Carole King\'s landmark 1971 album was titled simply:',
    options: ['Mosaic', 'Tapestry', 'Patchwork', 'Embroidery'],
    correctIndex: 1,
    blurb: 'It sat on the Billboard chart for over six years — a record for its time.',
    source: 'curated',
  },
  {
    id: 'c-m70-06',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'James Taylor sang "You\'ve Got a Friend" — but who wrote the song?',
    options: ['James Taylor', 'Carole King', 'Carly Simon', 'Joni Mitchell'],
    correctIndex: 1,
    blurb: 'King wrote it for her album Tapestry the same year — they\'re both definitive versions.',
    source: 'curated',
  },
  {
    id: 'c-m70-07',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'The Bee Gees dominated the soundtrack to which 1977 film?',
    options: ['Grease', 'Saturday Night Fever', 'Rocky', 'Foul Play'],
    correctIndex: 1,
    blurb: 'The brothers wrote five of the songs in a single weekend at a French chateau.',
    source: 'curated',
  },
  {
    id: 'c-m70-08',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Marvin Gaye\'s 1971 album about social ills opened with which song?',
    options: ["Let's Get It On", "What's Going On", 'Sexual Healing', 'I Heard It Through the Grapevine'],
    correctIndex: 1,
    blurb: 'Berry Gordy hated it at first — said it would never sell. It became Gaye\'s defining work.',
    source: 'curated',
  },
  {
    id: 'c-m70-09',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Fleetwood Mac\'s 1977 album of breakup songs was titled:',
    options: ['Tusk', 'Rumours', 'Mirage', 'Tango in the Night'],
    correctIndex: 1,
    blurb: 'Everyone in the band was breaking up with everyone else — and it sold 40 million copies.',
    source: 'curated',
  },
  {
    id: 'c-m70-10',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Linda Ronstadt\'s 1977 country-rock hit was "Blue":',
    options: ['Moon', 'Eyes', 'Bayou', 'Jeans'],
    correctIndex: 2,
    blurb: '"Blue Bayou" — a Roy Orbison original, but Ronstadt made it hers.',
    source: 'curated',
  },
  {
    id: 'c-m70-11',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Bruce Springsteen\'s 1975 breakthrough album was named after which city street?',
    options: ['Born to Run', 'Born in the U.S.A.', 'Thunder Road', 'Asbury Park'],
    correctIndex: 0,
    blurb: 'He spent half a year obsessing over the sax solo on the title track alone.',
    source: 'curated',
  },
  {
    id: 'c-m70-12',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'The Carpenters were a brother-sister duo. The sister\'s name was:',
    options: ['Karen', 'Sharon', 'Kristen', 'Carol'],
    correctIndex: 0,
    blurb: 'Karen — one of the warmest singing voices ever recorded in pop.',
    source: 'curated',
  },
  {
    id: 'c-m70-13',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'ABBA was from which Scandinavian country?',
    options: ['Norway', 'Sweden', 'Denmark', 'Finland'],
    correctIndex: 1,
    blurb: 'They won Eurovision in 1974 with "Waterloo" — and never looked back.',
    source: 'curated',
  },
  {
    id: 'c-m70-14',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'John Denver sang of country roads taking him home to which state?',
    options: ['Virginia', 'West Virginia', 'Tennessee', 'Kentucky'],
    correctIndex: 1,
    blurb: 'He\'d never been to West Virginia when he co-wrote it. Didn\'t matter.',
    source: 'curated',
  },
  {
    id: 'c-m70-15',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Diana Ross had a 1970 solo hit with "Ain\'t No Mountain":',
    options: ['Big Enough', 'High Enough', 'Cold Enough', 'Far Enough'],
    correctIndex: 1,
    blurb: 'Marvin Gaye and Tammi Terrell had done it first — Ross made the long version a classic.',
    source: 'curated',
  },
  {
    id: 'c-m70-16',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Earth, Wind & Fire put a "Boogie" where in their 1977 hit?',
    options: ['On the Floor', 'In Your Heart', 'Wonderland', 'Down'],
    correctIndex: 2,
    blurb: '"Boogie Wonderland" — that horn section gave goosebumps.',
    source: 'curated',
  },
  {
    id: 'c-m70-17',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Three Dog Night sang that Jeremiah was a what?',
    options: ['Cat', 'Dog', 'Bullfrog', 'Mailman'],
    correctIndex: 2,
    blurb: '"Joy to the World" — Hoyt Axton wrote it for a kids\' TV special that never aired.',
    source: 'curated',
  },
  {
    id: 'c-m70-18',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'David Bowie released which alien-rock-star concept album in 1972?',
    options: ['Aladdin Sane', 'The Rise and Fall of Ziggy Stardust', 'Hunky Dory', 'Diamond Dogs'],
    correctIndex: 1,
    blurb: 'He killed off Ziggy onstage in London a year later — a famous final-show announcement.',
    source: 'curated',
  },
  {
    id: 'c-m70-19',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Don McLean\'s 1971 epic was about "the day the music":',
    options: ['Died', 'Stopped', 'Faded', 'Stilled'],
    correctIndex: 0,
    blurb: '"American Pie" — eight and a half minutes long, every radio station played it whole.',
    source: 'curated',
  },
  {
    id: 'c-m70-20',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Roberta Flack won Record of the Year in 1972 with "The First Time Ever I Saw Your":',
    options: ['Smile', 'Face', 'Eyes', 'Heart'],
    correctIndex: 1,
    blurb: 'Clint Eastwood used it in "Play Misty for Me" — and it shot up the charts overnight.',
    source: 'curated',
  },
  {
    id: 'c-m70-21',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Helen Reddy\'s 1972 feminist anthem began "I am" what?',
    options: ['Strong', 'Woman', 'Free', 'Loud'],
    correctIndex: 1,
    blurb: '"I Am Woman" — she co-wrote it because nothing else like it was on the radio.',
    source: 'curated',
  },
  {
    id: 'c-m70-22',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Which Pink Floyd album spent over 700 weeks on the Billboard 200?',
    options: ['Wish You Were Here', 'The Dark Side of the Moon', 'Animals', 'The Wall'],
    correctIndex: 1,
    blurb: 'Released in 1973 — still on the charts when most people\'s kids were having kids.',
    source: 'curated',
  },
  {
    id: 'c-m70-23',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Olivia Newton-John starred opposite John Travolta in which 1978 musical film?',
    options: ['Saturday Night Fever', 'Grease', 'Hair', 'The Wiz'],
    correctIndex: 1,
    blurb: 'She was 29 playing a high-schooler — and absolutely got away with it.',
    source: 'curated',
  },
  {
    id: 'c-m70-24',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Which 1979 disco hit told a story of the village of the YMCA?',
    options: ['I Will Survive', 'Y.M.C.A.', 'Hot Stuff', 'Le Freak'],
    correctIndex: 1,
    blurb: 'The Village People — the YMCA itself initially sued, then realized membership was up.',
    source: 'curated',
  },
  {
    id: 'c-m70-25',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Gloria Gaynor declared in 1978 that she would what?',
    options: ['Survive', 'Forgive', 'Move On', 'Be Free'],
    correctIndex: 0,
    blurb: '"I Will Survive" — written in 20 minutes by two staff writers nobody had heard of.',
    source: 'curated',
  },
  {
    id: 'c-m70-26',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Glen Campbell sang about being which kind of cowboy?',
    options: ['Lonesome', 'Rhinestone', 'Outlaw', 'Country'],
    correctIndex: 1,
    blurb: '"Rhinestone Cowboy" — Larry Weiss wrote it years earlier, Campbell heard it on the radio.',
    source: 'curated',
  },
  {
    id: 'c-m70-27',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Cat Stevens (later Yusuf Islam) had a 1971 hit about which family relation?',
    options: ['Mother', 'Father and Son', 'Brother', 'Sister'],
    correctIndex: 1,
    blurb: '"Father and Son" — he sang both parts, with a different vocal tone for each.',
    source: 'curated',
  },
  {
    id: 'c-m70-28',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Which 1973 song had Tony Orlando hoping for a ribbon "round the ole oak tree"?',
    options: ['Yellow', 'Red', 'Blue', 'White'],
    correctIndex: 0,
    blurb: 'Sold three million copies — and ribbons started appearing on actual oak trees.',
    source: 'curated',
  },
  {
    id: 'c-m70-29',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Barry Manilow\'s 1974 hit was named after which woman?',
    options: ['Sadie', 'Mandy', 'Lola', 'Cathy'],
    correctIndex: 1,
    blurb: 'The original British version was called "Brandy" — they changed it to avoid confusion.',
    source: 'curated',
  },
  {
    id: 'c-m70-30',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Queen\'s 1975 rhapsody was named after which language or style?',
    options: ['Italian', 'Bohemian', 'Operatic', 'Spanish'],
    correctIndex: 1,
    blurb: '"Bohemian Rhapsody" — radio stations called it "career suicide." It hit number one anyway.',
    source: 'curated',
  },
  {
    id: 'c-m70-31',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Paul Simon\'s 1975 album was titled "Still Crazy After All These":',
    options: ['Days', 'Years', 'Nights', 'Songs'],
    correctIndex: 1,
    blurb: 'Won Album of the Year — he\'d been "still crazy" for about ten years by then.',
    source: 'curated',
  },
  {
    id: 'c-m70-32',
    category: '1970s Music',
    categorySlug: 'music-70s',
    prompt: 'Which sister duo had a 1978 hit "He\'s the Greatest Dancer" and another with "We Are Family"?',
    options: ['The Pointer Sisters', 'Sister Sledge', 'The Emotions', 'LaBelle'],
    correctIndex: 1,
    blurb: 'Sister Sledge — four real sisters from Philadelphia, produced by Chic\'s Nile Rodgers.',
    source: 'curated',
  },
];
